import { Alert, Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useCaseRequests } from '../api/cases';
import { useHasPermission } from '../api/session';

interface Props {
  caseId: string;
}

/**
 * Permission-gated entry card for the client API request log. Renders nothing
 * unless the viewer holds `case_requests:read`. When permitted, shows a compact
 * summary with a total count badge and a link to the dedicated request log page.
 */
export function CaseRequestsCard({ caseId }: Props) {
  const canView = useHasPermission('case_requests:read');
  // Only fire the query when the viewer can see the data — passing `undefined`
  // disables the underlying useQuery.
  const query = useCaseRequests(canView ? caseId : undefined);

  if (!canView) return null;

  const total = (query.data?.case_requests.length ?? 0) + (query.data?.client_session.length ?? 0);

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs" align="center">
            <Title order={5}>Client API Requests</Title>
            {query.data && <Badge variant="light">{total}</Badge>}
          </Group>
          <Button component={Link} to={`/cases/${caseId}/requests`} variant="subtle" size="xs">
            View all
          </Button>
        </Group>

        {query.isLoading ? (
          <Text c="dimmed">Loading…</Text>
        ) : query.isError ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Could not load requests.
          </Alert>
        ) : total === 0 ? (
          <Text c="dimmed">No client requests captured for this case yet.</Text>
        ) : (
          <Text c="dimmed" size="sm">
            {query.data?.case_requests.length} case request(s), {query.data?.client_session.length}{' '}
            session request(s).
          </Text>
        )}
      </Stack>
    </Card>
  );
}
